import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { BASE_URL } from "../config";
import { getAuthHeaders } from "../services/useAuth";
import CustomButton from "./CustomButton";
import Loader from "../dist/Loader";
import LoadingDropdown from "../utils/LoadingDropdown";

const SelectEmployee = () => {
  const navigate = useNavigate();
  const [filters, setFilters] = useState({
    state: "all",
    division: "all",
    designation: "all",
    status: "WORKING",
  });

  const [employees, setEmployees] = useState([]);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [showTable, setShowTable] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const [states, setStates] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [designations, setDesignations] = useState([]);

  const [isFetchingStates, setIsFetchingStates] = useState(false);
  const [isFetchingDivisions, setIsFetchingDivisions] = useState(false);
  const [isFetchingDesignations, setIsFetchingDesignations] = useState(false);

  // Fetch states and divisions once on mount
  useEffect(() => {
    const fetchInitialData = async () => {
      setIsFetchingStates(true);
      setIsFetchingDivisions(true);

      try {
        console.log("🔄 Fetching states and divisions...");

        const [stateRes, divisionRes] = await Promise.all([
          axios.get(`${BASE_URL}/api/states`, { headers: getAuthHeaders() }),
          axios.get(`${BASE_URL}/api/divisions`, { headers: getAuthHeaders() }),
        ]);

        console.log("✅ States API Response:", stateRes.data);
        console.log("✅ Divisions API Response:", divisionRes.data);

        const stateData = stateRes.data
          .map((item) => ({
            id: item.id || item.state_id,
            name: item.name || item.state_name || item.state || "",
          }))
          .filter((item) => item.name);
        setStates(stateData);

        if (divisionRes.data && Array.isArray(divisionRes.data)) {
          const divisionData = divisionRes.data.map((item) => ({
            id: item.id || item.division_id,
            name: item.name || item.division_name || "",
          }));
          setDivisions(divisionData);
        } else {
          console.error("❌ Unexpected divisions response format:", divisionRes.data);
          setDivisions([]);
        }
      } catch (err) {
        console.error("❌ Error fetching states/divisions:", err);
        setStates([]);
        setDivisions([]);
      } finally {
        setIsFetchingStates(false);
        setIsFetchingDivisions(false);
      }
    };

    fetchInitialData();
  }, []);

  // Fetch designations when state changes
  useEffect(() => {
    const fetchDesignations = async () => {
      if (!filters.state) {
        setDesignations([]);
        setFilters((prev) => ({ ...prev, designation: "all" }));
        return;
      }

      setIsFetchingDesignations(true);
      try {
        console.log(`🔄 Fetching designations for state ID: ${filters.state}`);

        const res = await axios.get(
          `${BASE_URL}/api/designations/?state_id=${encodeURIComponent(filters.state)}`,
          { headers: getAuthHeaders() },
        );

        console.log(`✅ Designations API Response for state ${filters.state}:`, res.data);

        if (res.data && Array.isArray(res.data)) {
          const designationList = res.data.map((item) => ({
            id: item.id || item.designation_id,
            name: item.name || item.designation_name || "",
          }));
          setDesignations(designationList);
        } else {
          console.error("❌ Unexpected designations response format:", res.data);
          setDesignations([]);
        }
      } catch (err) {
        console.error("❌ Error fetching designations:", err);
        setDesignations([]);
      } finally {
        setIsFetchingDesignations(false);
      }
    };

    fetchDesignations();
  }, [filters.state]);

  // Fetch employees with filters as API parameters
  const fetchEmployeesWithFilters = async (filterParams) => {
    try {
      const params = new URLSearchParams();
      if (filterParams.state && filterParams.state !== "all") {
        params.append("state_id", filterParams.state);
      }
      if (filterParams.division && filterParams.division !== "all") {
        const selectedDivision = divisions.find(
          (div) => div.id.toString() === filterParams.division.toString(),
        );
        if (selectedDivision) {
          params.append("division", selectedDivision.name);
        }
      }
      if (filterParams.designation && filterParams.designation !== "all") {
        params.append("designation", filterParams.designation);
      }
      if (filterParams.status) params.append("status", filterParams.status);

      const url = `${BASE_URL}/api/employees?${params.toString()}`;
      const response = await axios.get(url, { headers: getAuthHeaders() });

      if (response.data && Array.isArray(response.data)) {
        return response.data;
      } else {
        console.error("❌ Unexpected employees response format:", response.data);
        return [];
      }
    } catch (error) {
      console.error("❌ Error fetching employees:", error);
      return [];
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => {
      if (name === "state") {
        return { ...prev, state: value, designation: "all" };
      }
      return { ...prev, [name]: value };
    });
  };

  // Helper function to get display name for filters summary
  const getDisplayName = (items, id, defaultText = "N/A") => {
    if (!id || id === "all") return defaultText;
    const item = items.find((item) => item.id.toString() === id.toString());
    return item ? item.name : defaultText;
  };

  // Helper function to get designation display name (since we use names as values)
  const getDesignationDisplayName = (designationValue, defaultText = "N/A") => {
    if (!designationValue || designationValue === "all") return defaultText;
    const designation = designations.find((d) => d.name === designationValue);
    return designation ? designation.name : designationValue;
  };

  const handleSearch = async () => {
    setIsLoading(true);
    setShowTable(false);

    try {
      console.log("🔍 Starting search with filters:", filters);
      const filteredEmployeesData = await fetchEmployeesWithFilters(filters);
      setFilteredEmployees(filteredEmployeesData);
      setSelectedFilters({ ...filters });
      setShowTable(true);
    } catch (error) {
      console.error("❌ Error during search:", error);
      alert("Error searching employees. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="p-2 border-2 border-gray-300 rounded-lg bg-white">
      {isLoading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/25">
          <Loader onCancel={() => setIsLoading(false)} />
        </div>
      )}
      <div className="w-full">
        <div className="p-4 border-2 border-gray-300 rounded-lg bg-gray-50">
          {/* Title */}
          <h1 className="text-xl sm:text-2xl font-bold text-center text-gray-800 mb-4">
            <span className="text-blue-700">Employee </span>
            <span className="text-blue-500">Details</span>
          </h1>

          {/* Row 1 - State & Designation */}
          <div className="punchedAttendence-filter-row1">
            <LoadingDropdown
              label="State"
              name="state"
              value={filters.state}
              onChange={handleChange}
              isLoading={isFetchingStates}
              showSpinnerOnLabel={false}
            >
              <option value="all">All</option>
              {states.map((state) => (
                <option key={state.id} value={state.id}>{state.name}</option>
              ))}
            </LoadingDropdown>

            <LoadingDropdown
              label="Designation"
              name="designation"
              value={filters.designation}
              onChange={handleChange}
              isLoading={isFetchingDesignations}
            >
              <option value="all">All</option>
              {designations.map((d) => (
                <option key={d.id} value={d.name}>{d.name}</option>
              ))}
            </LoadingDropdown>
          </div>

          {/* Row 2 - Division & Status */}
          <div className="punchedAttendence-filter-row2">
            <LoadingDropdown
              label="Division"
              name="division"
              value={filters.division}
              onChange={handleChange}
              isLoading={isFetchingDivisions}
            >
              <option value="all">All</option>
              {divisions.map((d) => (
                <option key={d.id} value={d.id}>{d.name.toUpperCase()}</option>
              ))}
            </LoadingDropdown>

            <LoadingDropdown
              label="Status"
              name="status"
              value={filters.status}
              onChange={handleChange}
              isLoading={false}
            >
              <option value="WORKING">WORKING</option>
              <option value="RESIGNED">RESIGNED</option>
              <option value="RESIGNED NOT EXPELLED">RESIGNED NOT EXPELLED</option>
              <option value="EXPELLED NETWORK">EXPELLED NETWORK</option>
            </LoadingDropdown>
          </div>

          {/* Search Button */}
          <div className="flex justify-center mt-4">
            <CustomButton
              variant="save"
              onClick={handleSearch}
              disabled={isLoading || !filters.state}
            >
              {isLoading ? "Loading..." : "Go"}
            </CustomButton>
          </div>
        </div>

        {/* Results Table */}
        {showTable && !isLoading && (
          <div className="p-2 border-2 border-gray-300 rounded-lg bg-white mt-5">
            {filteredEmployees.length > 0 ? (
              <div className="border rounded shadow table-wrapper">
                <table className="unlock-dcr-leave-table text-sm">
                  <thead className="bg-blue-500 text-white">
                    <tr>
                      <th className="border px-2 py-1">S.No</th>
                      <th className="border px-2 py-1">EMP ID</th>
                      <th className="border px-2 py-1">State</th>
                      <th className="border px-2 py-1">Employee Name</th>
                      <th className="border px-2 py-1">City</th>
                      <th className="border px-2 py-1">Division</th>
                      <th className="border px-2 py-1">Phone</th>
                      <th className="border px-2 py-1">Manager Name</th>
                      <th className="border px-2 py-1">Status</th>
                      <th className="border px-2 py-1">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredEmployees.map((emp, index) => (
                      <tr key={emp.id} className="hover:bg-gray-50">
                        <td className="border px-2 py-1 text-center">{index + 1}</td>
                        <td className="border px-2 py-1 text-center">{emp.employee}</td>
                        <td className="border px-2 py-1">{emp.state_name || "N/A"}</td>
                        <td className="border px-2 py-1">
                          {emp.first_name || ""} {emp.last_name || ""}
                        </td>
                        <td className="border px-2 py-1">{emp.city_name || "N/A"}</td>
                        <td className="border px-2 py-1">{emp.division || "N/A"}</td>
                        <td className="border px-2 py-1">{emp.mobile || "N/A"}</td>
                        <td className="border px-2 py-1">{emp.manager || "N/A"}</td>
                        <td className="border px-2 py-1 text-center">
                          {emp.status === "WORKING" ? (
                            <span className="text-green-600 font-bold" title="Working">✔</span>
                          ) : (
                            <span className="text-red-600 font-bold" title={emp.status}>❌</span>
                          )}
                        </td>
                        <td className="border px-2 py-1 text-center">
                          <button
                            className="bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700 transition-colors"
                            onClick={() =>
                              window.open(
                                `/edit-employee/${emp.id}`,
                                "EditEmployeeWindow",
                                "width=1200,height=800,top=100,left=100,scrollbars=yes,resizable=yes",
                              )
                            }
                            title="Edit Employee"
                          >
                            Edit
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center text-red-500 mt-4 p-4 bg-red-50 rounded-lg">
                No employees found matching your criteria
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SelectEmployee;